import { cloneElement, forwardRef, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react"
import { motion, useMotionValue, useReducedMotion, useScroll } from "framer-motion"
import { css } from "@emotion/react"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { useNav } from "components/nav-v2/Wrapper"

import generateComponents from "utils/generateComponents"

import { waTriggerEvent } from "components/analytics/DigitalDataLayer"

import Arrows from "./Arrows"
import { RollerProvider, useRoller } from "./context"
import { cssStartVar, startCol } from "./cssvars"
import { reduceItemsForGenerateComponents } from "./perso"

const INSTALLATION_DURATION_MS = 1400
const INSTALLATION_STAGGER_MS = 160

const startVar = css`
  ${cssStartVar}: 0;

  &.${startCol["first-col"]} {
    ${cssStartVar}: 0;
  }

  ${getMediaQuery("m")} {
    &.${startCol["third-col"]} {
      ${cssStartVar}: 1;
    }
    &.${startCol["fourth-col"]} {
      ${cssStartVar}: 2;
    }
  }

  ${getMediaQuery("l")} {
    &.${startCol["second-col"]} {
      ${cssStartVar}: 1;
    }
    &.${startCol["third-col"]} {
      ${cssStartVar}: 2;
    }

    &.${startCol["fourth-col"]} {
      ${cssStartVar}: 3;
    }
  }

  /* ${getMediaQuery("xxl")} {
    &.${startCol["first-col"]} {
      ${cssStartVar}: 1;
    }
    &.${startCol["second-col"]} {
      ${cssStartVar}: 2;
    }

    &.${startCol["third-col"]} {
      ${cssStartVar}: 3;
    }
  } */
`

const Container = styled(motion.section)`
  ${startVar}
  --hero-overlay-width: min(100vw, 1920px);
  --outer-margin: calc(0.08 * var(--hero-overlay-width));
  --grid-col-unit: calc(((var(--hero-overlay-width) - var(--outer-margin) * 2) - (var(--grid-gap) * (var(--grid-col-num) - 1))) / var(--grid-col-num));
  --dir: 1;
  &:dir(rtl) {
    --dir: -1;
  }

  ${fullGrid}
  height: 100%;
  width: 100%;

  grid-template-rows: [roller] auto;
  /* opacity: 0; */
  pointer-events: none;
  /* transition: opacity 360ms; */

  &.show {
    /* opacity: 1; */
    pointer-events: auto;
  }

  &.is-hidden {
    li,
    li figure {
      transition-delay: 0ms !important;
    }
  }

  html.prefers-reduced-motion & {
    transition: unset;
  }
  @media (prefers-reduced-motion) {
    transition: unset;
  }
`

const Nav = styled.nav`
  grid-column: doc;
  grid-row: roller;
  ${fullGrid}
  width: 100%;
`

const List = styled.ul`
  grid-column: doc;
  grid-row: roller;
  display: grid;
  grid-auto-flow: column;
  gap: var(--grid-gap);
  height: 100vh;
  align-content: center;

  overflow-x: auto;
  overflow-y: hidden;

  padding-inline-start: calc(var(--outer-margin) + var(--grid-gap) * var(${cssStartVar}) + var(--grid-col-unit) * var(${cssStartVar}));

  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.s-col-roller {
    --col-nb: 5;
    ${getMediaQuery("l")} {
      --col-nb: 3;
    }
  }
  &.m-col-roller {
    --col-nb: 5;
    ${getMediaQuery("l")} {
      --col-nb: 4;
    }
  }
  &.l-col-roller {
    --col-nb: 6;
    ${getMediaQuery("m")} {
      --col-nb: 7;
    }
    ${getMediaQuery("l")} {
      --col-nb: 5;
    }
  }
  &.xl-col-roller {
    --col-nb: 6;
    ${getMediaQuery("m")} {
      --col-nb: 8;
    }
    ${getMediaQuery("l")} {
      --col-nb: 6;
    }
  }
  &.y-col-roller {
    --col-nb: 5;
    ${getMediaQuery("m")} {
      --col-nb: 5;
    }
    ${getMediaQuery("l")} {
      --col-nb: 4;
    }
  }

  grid-auto-columns: calc(var(--grid-col-unit) * var(--col-nb) + var(--grid-gap) * (var(--col-nb) - 1));

  & figure img {
    width: 100%;
    height: auto;
  }

  & li {
    scroll-snap-align: center;
  }

  & li:first-of-type {
    html:not([dir="rtl"]) & {
      scroll-snap-align: start;
    }

    scroll-margin-inline-start: calc(var(--outer-margin) + var(--grid-gap) * var(${cssStartVar}) + var(--grid-col-unit) * var(${cssStartVar}));
  }

  & li:last-of-type {
    scroll-snap-align: end;
    scroll-margin-inline-end: var(--outer-margin);
    padding-inline-end: var(--outer-margin);
  }

  // to prevent cards focus outline to be cut :
  padding-block-start: 10px;
  margin-block-start: -10px;
  padding-block-end: 20px;
  margin-block-end: -10px;
  //////////////////////////////////////////////
`

function Strip({ className, items, labelledBy, ariaLabel, nav, installationVisible = false }, ref) {
  const console = useConsole()
  const { isMobile } = useViewport()

  const reducedItems = useMemo(() => reduceItemsForGenerateComponents(items), [items])
  const [showArrows, setShowArrows] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [installationComplete, setInstallationComplete] = useState(false)
  const activeCurrentIndex = installationComplete ? currentIndex : 0

  const { max, unit, isTouchDevice, assetCenter } = useRoller()
  const { scrollX } = useScroll({ container: ref })

  const modularBlocks = useMemo(() => {
    const itemsWithCurrentState = reducedItems.map((item, index) => {
      if (isValidElement(item)) return cloneElement(item, { currentIndex: activeCurrentIndex })
      return { ...item, isCurrent: index === activeCurrentIndex }
    })

    return generateComponents(itemsWithCurrentState, { console })
  }, [activeCurrentIndex, console, reducedItems])

  const updateCurrentIndex = useCallback(() => {
    const list = ref.current
    if (!list?.children?.length) return
    if (!isMobile.get()) {
      setCurrentIndex(0)
      return
    }

    const listRect = list.getBoundingClientRect()
    const center = listRect.left + listRect.width / 2

    let nextIndex = 0
    let minDistance = Number.POSITIVE_INFINITY

    Array.from(list.children).forEach((item, index) => {
      const rect = item.getBoundingClientRect()
      const distance = Math.abs(rect.left + rect.width / 2 - center)
      if (distance < minDistance) {
        minDistance = distance
        nextIndex = index
      }
    })

    setCurrentIndex(nextIndex)
  }, [isMobile, ref])

  const updateUnit = useCallback(() => {
    max.set(ref.current.scrollWidth - ref.current.clientWidth - unit.get() / 2)
    setShowArrows(max.get() > 0)
    updateCurrentIndex()
  }, [max, ref, unit, updateCurrentIndex])

  const onWchange = () => updateUnit()
  useEffect(() => unit.onChange(onWchange))

  useLayoutEffect(() => {
    const r = ref?.current

    if (r?.children.length) {
      unit.set(r?.children[0]?.offsetWidth)
      assetCenter.set(r?.children[0]?.getElementsByTagName("figure")[0]?.children[0]?.offsetHeight / 2)
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize && r?.children.length) {
          unit.set(r?.children[0].offsetWidth)
          assetCenter.set(r?.children[0].getElementsByTagName("figure")[0]?.children[0]?.offsetHeight / 2)
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  useLayoutEffect(() => {
    updateUnit()
  }, [])

  useEffect(() => {
    const list = ref.current
    if (!list) return

    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(updateCurrentIndex)
    }

    updateCurrentIndex()
    list.addEventListener("scroll", onScroll, { passive: true })
    const unsubscribe = isMobile.onChange(() => requestAnimationFrame(updateCurrentIndex))

    return () => {
      cancelAnimationFrame(raf)
      list.removeEventListener("scroll", onScroll)
      unsubscribe?.()
    }
  }, [isMobile, reducedItems, updateCurrentIndex])

  useEffect(() => {
    if (!installationVisible) {
      setInstallationComplete(false)
      return
    }

    const timeout = window.setTimeout(() => {
      updateCurrentIndex()
      setInstallationComplete(true)
    }, INSTALLATION_DURATION_MS + Math.max(reducedItems.length - 1, 0) * INSTALLATION_STAGGER_MS)

    return () => window.clearTimeout(timeout)
  }, [installationVisible, reducedItems.length, updateCurrentIndex])

  return (
    <>
      <List ref={ref} className={className} aria-labelledby={labelledBy} aria-label={ariaLabel} tabIndex={-1}>
        {modularBlocks}
      </List>
      {showArrows && !isTouchDevice && installationVisible ? <Arrows ref={ref} scrollX={scrollX} /> : null}
    </>
  )
}

const StripWithRef = forwardRef(Strip)

function RollerContent({
  column_width,
  // components,
  className,
  texts,
  background,
  start_column,
  nav,
  items,
  containerRef,
  installationVisible = false,
}) {
  const console = useConsole()
  const cnav = useNav()
  const reducedMotion = useReducedMotion()
  // const imageAlts = components.map(item => components.image_card?.alt).filter(Boolean)
  const ref = useRef()

  const [installationState, setInstallationState] = useState("is-pristine")
  const installationStateRef = useRef("is-pristine")
  const itemCount = items?.length ?? 0

  const updateInstallationState = nextState => {
    installationStateRef.current = nextState
    setInstallationState(nextState)
  }

  const bgcol = background ? background.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`) : []
  const backgroundColor = useMotionValue(bgcol.length === 1 ? bgcol[0] : bgcol.length > 1 ? `linear-gradient(${bgcol})` : null)
  const startClass = start_column ? ` ${startCol[start_column]}` : ` ${startCol["first-col"]}`

  const labelledBy = texts?.length ? texts.find(o => "heading" in o)?.heading?._metadata.uid : null
  const ariaLabel = texts?.length ? texts.find(o => "heading" in o)?.heading : null

  // function triggerWA() {
  //   const rollerRefInfo = cards.map((card, index) => ({
  //     cardName: card.slug?.toLowerCase().replace(/-/g, " ") || "",
  //     cardPosition: index + 1,
  //   }))

  //   waTriggerEvent({
  //     eventName: "rollerRefDisplayed",
  //     eventType: "Impression",
  //     rollerRefInfo,
  //   })
  // }

  useEffect(() => {
    const el = ref.current

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        // triggerWA()
        observer.disconnect()
      }
    })

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const list = ref.current
    if (!list) return

    if (reducedMotion) {
      updateInstallationState(installationVisible ? "show" : "is-pristine")
      return
    }

    let releases = []
    let timeout = null

    const lockNavScrollFor = duration => {
      const releaseMain = cnav?.main?.lock?.request?.(0)
      const releaseSub = cnav?.sub?.lock?.request?.(0)

      releases = [releaseMain, releaseSub].filter(Boolean)
      timeout = window.setTimeout(() => {
        for (const release of releases) {
          release()
        }
        releases = []
      }, duration)
    }

    if (!installationVisible) {
      const nextState = installationStateRef.current === "show" ? "is-hidden" : "is-pristine"
      updateInstallationState(nextState)

      return () => {
        if (timeout) window.clearTimeout(timeout)
        for (const release of releases) {
          release()
        }
      }
    }

    updateInstallationState("is-pristine")
    // lockNavScrollFor(INSTALLATION_DURATION_MS + Math.max(itemCount - 1, 0) * INSTALLATION_STAGGER_MS)
    const raf = requestAnimationFrame(() => {
      list?.scrollTo({
        top: 0,
        left: 0,
      })
      updateInstallationState("show")
    })
    return () => {
      cancelAnimationFrame(raf)
      if (timeout) window.clearTimeout(timeout)
      for (const release of releases) {
        release()
      }
    }
  }, [installationVisible, itemCount, nav, reducedMotion])

  return (
    <RollerProvider>
      <Container ref={containerRef} className={`${className}${startClass} ${installationState}`} style={{ background: backgroundColor }}>
        {nav ? (
          <Nav aria-label={nav}>
            <StripWithRef ref={ref} className={column_width} items={items} nav={nav} installationVisible={installationState === "show"} />
          </Nav>
        ) : (
          <StripWithRef
            ref={ref}
            className={column_width}
            items={items}
            labelledBy={labelledBy}
            ariaLabel={ariaLabel}
            installationVisible={installationState === "show"}
          />
        )}
      </Container>
    </RollerProvider>
  )
}

const Roller = forwardRef(function Roller({ ...props }, ref) {
  const console = useConsole()
  console.log("HeroMediaOverlay Roller", props)

  return <RollerContent {...props} containerRef={ref} />
})

export default Roller
